import type { CustomerJourney, JourneyNote } from '@/api/customer-journey';
import { STEP_LABELS } from '../sale-process-helpers';

/**
 * Shared model for the interactive Pipeline board — a 1-1 rebuild of the legacy
 * SaleProcessBoard kanban. The board shows only the four pipeline steps (the
 * terminal close_win/close_lose/terminate outcomes are conveyed by the journey
 * status, not their own columns).
 */

export type StageStatus = 'completed' | 'current' | 'pending' | 'disabled';
export type JourneyOutcome = 'active' | 'won' | 'lost' | 'terminated';

/** The four columns, in pipeline order. */
export const BOARD_STEPS: { value: string; label: string }[] = [
  { value: 'searching', label: STEP_LABELS.searching },
  { value: 'presentation', label: STEP_LABELS.presentation },
  { value: 'poc', label: STEP_LABELS.poc },
  { value: 'contract', label: STEP_LABELS.contract },
];

const BOARD_STEP_VALUES = BOARD_STEPS.map((s) => s.value);

/** Legacy app still owns the create-flows (POC/business/ticket) + service pages. */
export const LEGACY_APP_BASE = 'https://dc.vnetwork.dev';

/** Build an absolute legacy-app URL with a query string (for window.open). */
export function legacyUrl(path: string, query: Record<string, string | number | undefined> = {}): string {
  const qs = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  return `${LEGACY_APP_BASE}${path}${qs ? `?${qs}` : ''}`;
}

/** Overall outcome of a journey (mirrors legacy getJourneyOutcome). */
export function journeyOutcome(journey: CustomerJourney): JourneyOutcome {
  if (!journey.customer_journey_locked) return 'active';
  const step = journey.customer_journey_step;
  if (step === 'close_win' || (step === 'contract' && journey.customer_journey_step_status === 'completed')) {
    return 'won';
  }
  if (step === 'terminate') return 'terminated';
  return 'lost';
}

/** Column status relative to the journey's current step + outcome. */
export function stageStatus(stepIndex: number, currentIndex: number, outcome: JourneyOutcome): StageStatus {
  if (outcome === 'won') return 'completed';
  if (outcome === 'lost' || outcome === 'terminated') return 'disabled';
  if (stepIndex < currentIndex) return 'completed';
  if (stepIndex === currentIndex) return 'current';
  return 'pending';
}

/** Index of the journey's current step within the board columns (-1 if terminal). */
export function currentStageIndex(journey: CustomerJourney): number {
  return BOARD_STEP_VALUES.indexOf(String(journey.customer_journey_step));
}

/**
 * System notes are auto-generated step/lifecycle markers; the board hides them
 * from the human notes list (they'd be noise). Ported from legacy isSystemNote.
 */
const SYSTEM_NOTE_PATTERNS: RegExp[] = [
  /^Chuyển sang bước/i,
  /^Chuyển từ .+ sang/i,
  /^Moved to /i,
  /^Moved from .+ to/i,
  /^Step changed to/i,
  /^Hoàn thành quá trình/i,
  /^Journey completed/i,
  /^Mở lại từ/i,
  /^Re-engaged from/i,
  /^Đóng deal/i,
  /^Deal closed/i,
  /^Tạo POC/i,
  /^POC created/i,
  /^Tạo hợp đồng/i,
  /^Contract created/i,
  /^step-changed-to/i,
  /^EMPLOYEE:\d+\.ORDER:/i,
];

export function isSystemJourneyNote(content?: string): boolean {
  if (!content) return false;
  return SYSTEM_NOTE_PATTERNS.some((re) => re.test(content.trim()));
}

/* ── Note bucketing (all from the embedded journey.customer_journey_notes) ── */

/** Plain human notes for a step (system + reference-linked notes excluded). */
export function stepNotes(notes: JourneyNote[], step: string): JourneyNote[] {
  return notes.filter(
    (n) =>
      n.customer_journey_note_step === step &&
      !n.reference_type &&
      !isSystemJourneyNote(n.customer_journey_note),
  );
}

/** POC trial services (reference_type: 'service-poc'). */
export function pocNotes(notes: JourneyNote[]): JourneyNote[] {
  return notes.filter((n) => n.reference_type === 'service-poc' && n.service_poc);
}

/** Linked business trips (reference_type: 'business'). */
export function businessNotes(notes: JourneyNote[]): JourneyNote[] {
  return notes.filter((n) => n.reference_type === 'business' && n.business);
}

/** Linked meeting tickets (reference_type: 'ticket'). */
export function ticketNotes(notes: JourneyNote[]): JourneyNote[] {
  return notes.filter((n) => n.reference_type === 'ticket' && n.ticket);
}

/** Contract services (reference_type: 'service'), de-duped by service_id. */
export function contractServiceNotes(notes: JourneyNote[]): JourneyNote[] {
  const seen = new Set<string>();
  const out: JourneyNote[] = [];
  for (const n of notes) {
    if (n.reference_type !== 'service' || !n.service) continue;
    const key = String(n.service.service_id ?? n.customer_journey_note_id);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(n);
  }
  return out;
}

/** True once a contract note has completed — hides the "Create contract" action. */
export function hasCompletedContract(notes: JourneyNote[]): boolean {
  return notes.some(
    (n) => n.customer_journey_note_step === 'contract' && n.customer_journey_note_step_status === 'completed',
  );
}

/** POC trial expiry, keyed off the service's stop date. */
export function pocExpiry(stoppedDate?: string | null): {
  status: 'ongoing' | 'active' | 'expiring' | 'expired';
  daysLeft: number | null;
  isWarning: boolean;
} {
  if (!stoppedDate?.trim()) return { status: 'ongoing', daysLeft: null, isWarning: false };
  const stop = new Date(stoppedDate);
  if (Number.isNaN(stop.getTime())) return { status: 'ongoing', daysLeft: null, isWarning: false };
  const daysLeft = Math.floor((stop.getTime() - Date.now()) / 86_400_000);
  if (daysLeft < 0) return { status: 'expired', daysLeft, isWarning: true };
  if (daysLeft <= 3) return { status: 'expiring', daysLeft, isWarning: true };
  return { status: 'active', daysLeft, isWarning: false };
}
